import { StyleSheet } from "@react-pdf/renderer";

export const COLORS = {
    black: "#000000",
    blue: "#1e449a",
    red: "#ff0000",
    gray: "#c0c0c0",
    white: "#ffffff",
    grayLine: "#afafaf",
};

// Single source of truth for the form's line weight.
export const BORDER = 1;

export const styles = StyleSheet.create({
    page: {
        paddingVertical: 16,
        paddingHorizontal: 18,
        fontFamily: "Helvetica",
        fontSize: 6,
        color: COLORS.black,
        backgroundColor: COLORS.white,
    },
    // Outer bordered form container (legacy `.body`). Fills the page height so
    // the footer lands at the bottom edge; a single `fillRow` per page absorbs
    // the slack (single-target grow — stable, unlike distributing across rows).
    body: {
        flexGrow: 1,
        borderWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    // The one section per page that stretches to consume leftover height.
    fillRow: { flexGrow: 1 },

    // ---- Title bar (legacy `.titleContainer` / `.titleWrapper`) ----
    titleContainer: {
        flexDirection: "row",
        backgroundColor: COLORS.gray,
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        alignItems: "stretch",
    },
    titleSubtitleWrapper: {
        flex: 1,
        textAlign: "center",
        justifyContent: "center",
        paddingVertical: 4,
        paddingHorizontal: 6,
    },
    titleH1: {
        fontSize: 12,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
    },
    subtitle: {
        fontSize: 6,
        textAlign: "center",
        marginTop: 2,
    },
    rightSubContainer: {
        width: 110,
        borderLeftWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
    },
    titleRightSpan: {
        textAlign: "center",
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
    },

    // ---- Rows / cells ----
    row: {
        flexDirection: "row",
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    noBorderBottom: { borderBottomWidth: 0 },
    // Paired section-title rows must stay thin (don't absorb grid slack).
    titleRow: { flexGrow: 0 },
    borderRight: {
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    borderBottom: {
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    borderLeft: {
        borderLeftWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    // Explicit flexBasis:0 (not the shorthand's `auto`) so a cell's width is set
    // purely by its flex share, never its content. flexShrink:0 to MATCH fieldCell
    // exactly — if the two sides of a row shrink differently the 50% boundary
    // settles at slightly different x per row and the column divider jogs.
    fullFlex: { flexGrow: 1, flexShrink: 0, flexBasis: 0 },
    cell: {
        padding: 3,
        position: "relative",
    },
    cellSmallPadding: {
        padding: 2,
        position: "relative",
    },

    // ---- Section sub-title bars (legacy `.smallTitle`) ----
    smallTitle: {
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: COLORS.gray,
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        paddingVertical: 2,
        paddingHorizontal: 2,
        position: "relative",
    },
    smallTitleFlat: { borderBottomWidth: 0 },
    smallTitleH2: {
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
    },
    // Fixed-width wrapper keeps the sup-text on one line (an absolute <Text>
    // ignores `width` in react-pdf v2 and hyphen-wraps against the bar edge).
    titleSupWrap: {
        position: "absolute",
        right: 6,
        bottom: 1,
        width: 210,
    },
    titleSupText: {
        fontSize: 5,
        textAlign: "right",
    },

    // ---- Label / value field (legacy `.cellTextContainer`) ----
    // Grows to fill its row's width (flexBasis:0) but keeps a real minHeight so
    // it never collapses when stacked in a column. flexShrink:0 so a form that
    // is a touch too tall never compresses these rows into an overlap.
    // IMPORTANT: zero padding. Yoga treats flexBasis:0 as content-box, so a
    // cell's padding ADDS to its flex share — a padded cell paired with an
    // unpadded one splits off-center and the column divider jogs. All padding
    // lives on the inner fieldContainer instead.
    fieldCell: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        minHeight: 19,
        position: "relative",
        justifyContent: "center",
    },
    fieldCellHalf: {
        minHeight: 10,
    },
    fieldContainer: {
        flexDirection: "row",
        alignItems: "center",
        minHeight: 14,
        paddingHorizontal: 2,
    },
    fieldContainerHalf: {
        minHeight: 0,
    },
    halfFieldText: {
        lineHeight: 1,
    },
    // Outer size is half a standard row: fieldCell is 22 and the row rule is
    // 1, so a half row is (22 + 1) / 2 = 11.5, of which 1 is this row's own
    // rule. Six of these occupy the same space as the three rows they replace,
    // and fieldCell itself is unchanged for the rest of the form.
    halfRow: {
        height: 10.5,
        alignItems: "center",
    },
    fieldLabel: {
        fontSize: 7,
        width: 66,
        marginRight: 6,
    },
    fieldLabelAuto: {
        fontSize: 7,
        marginRight: 6,
    },
    fieldValue: {
        color: COLORS.blue,
        fontSize: 7,
        fontFamily: "Helvetica-Bold",
        flex: 1,
    },
    smallLabel: {
        fontSize: 6,
    },

    // ---- Field number tags (legacy `.smallNumberFields`) ----
    numField: {
        fontSize: 4.5,
        position: "absolute",
        bottom: 1,
        right: 2,
        color: COLORS.black,
    },

    // ---- Disclaimer name block (legacy `.nameContainer`) ----
    regText: {
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
    },
    nameContainer: {
        flexDirection: "row",
        alignItems: "flex-end",
        marginBottom: 3,
    },
    inputName: {
        flex: 1,
        height: 12,
        alignItems: "center",
        justifyContent: "flex-end",
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        marginLeft: 6,
    },
    nameContainerValue: {
        color: COLORS.red,
        fontFamily: "Helvetica-Bold",
        fontSize: 9,
    },

    // ---- Project number box (legacy `.projectNumber`) ----
    projectNumber: {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: 4,
        flexGrow: 1,
    },
    projectNumberSpan: {
        fontSize: 5.5,
        fontFamily: "Helvetica-Bold",
        marginBottom: 6,
    },
    projectNumberValue: {
        color: COLORS.blue,
        fontSize: 10,
        fontFamily: "Helvetica-Bold",
    },

    // ---- Checkbox selection groups (legacy `.selectionBox*`) ----
    selectionBoxContainer: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        flex: 1,
    },
    selectionBoxWrapper: {
        flexDirection: "row",
        alignItems: "center",
        flex: 1,
        position: "relative",
    },
    checkbox: {
        width: 12,
        height: 12,
        borderWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 4,
    },
    checkboxX: {
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
        lineHeight: 1,
    },
    boxText: {
        fontSize: 5.5,
    },
    // Inline field-number subscript after a checkbox label (e.g. g, h).
    boxNum: {
        fontSize: 4.5,
        marginLeft: 3,
        alignSelf: "flex-end",
        marginBottom: -1,
    },

    // ---- Value rectangles on the cover page (legacy `.rectangle`) ----
    textWithBoxContainer: {
        flexDirection: "row",
        alignItems: "center",
        marginVertical: 5,
        marginHorizontal: "auto",
        position: "relative",
    },
    textWithBox: {
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
        marginRight: 6,
    },
    rectangle: {
        width: 90,
        height: 18,
        borderWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        alignItems: "center",
        justifyContent: "center",
        marginHorizontal: 4,
    },
    heatText: {
        fontSize: 8,
        fontFamily: "Helvetica-Bold",
    },
    boxesContainer: {
        flexDirection: "row",
        alignItems: "center",
    },
    boxWithTextWrapper: {
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        marginHorizontal: 4,
    },

    // ---- Clause paragraphs (legacy `.textRowWithNumber`) ----
    textRowWithNumber: {
        flexDirection: "row",
        paddingHorizontal: 14,
        marginVertical: 3,
        fontSize: 7,
    },
    clauseNum: {
        width: 24,
        marginRight: 6,
        fontSize: 7,
        alignSelf: "flex-end",
    },
    clauseText: {
        flex: 1,
        fontSize: 7,
    },

    // ---- Two/three-column blocks (legacy `.columnA` / `.columnB`) ----
    // Grow-based thirds, zero padding, basis 0 — Yoga's content-box basis means
    // percent bases + borders overflow the row and drift the divider edges;
    // equal grow shares with borders as the only (constant 1pt) asymmetry keep
    // the 1/3 and 2/3 lines at the same x on every row, including the footer.
    columnA: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        justifyContent: "space-between",
    },
    columnPlain: { flexGrow: 1, flexShrink: 0, flexBasis: 0, justifyContent: "space-between" },
    // Wraps columnA + stampBox at grow 2 and draws the 2/3 divider itself, so
    // the row has the same in-flow border count as the footer (one) and both
    // 2/3 lines compute to the identical x (harness-verified: 401..402).
    calcLeftPair: {
        flexGrow: 2,
        flexShrink: 0,
        flexBasis: 0,
        flexDirection: "row",
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    stampBox: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        position: "relative",
    },

    // ---- Footer with logos (legacy footer row) ----
    footerRow: {
        flexDirection: "row",
    },
    // Grow 2:1 with basis 0 and zero horizontal padding — the 2/3 divider then
    // computes from the same flex math as the calc columns above, so 69's line
    // sits exactly under 68's (borders are the only, constant, asymmetry).
    footerLogoCell: {
        flexGrow: 2,
        flexShrink: 0,
        flexBasis: 0,
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        paddingVertical: 8,
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
    },
    footerHvacCell: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: 8,
    },
    footerLogo: {
        width: 120,
        objectFit: "contain",
    },
    footerHvacLogo: {
        width: 45,
        objectFit: "contain",
    },
    footnote: {
        position: "absolute",
        bottom: 1,
        left: 0,
        right: 0,
        textAlign: "center",
        fontSize: 5.5,
    },

    // ---- Room-by-room table ----
    rxrIndexCell: {
        width: 26,
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        fontSize: 5.5,
    },
    rxrHeaderText: {
        color: COLORS.black,
        fontSize: 6,
        fontFamily: "Helvetica-Bold",
        textAlign: "center",
    },
    rxrDataRow: {
        flexDirection: "row",
        borderBottomWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        minHeight: 13,
        // Uniform rows share leftover height so the table fills the page evenly.
        flexGrow: 1,
    },
    // Zero padding on the flex cells (content-box basis — see fieldCell note);
    // the text carries the inset as margin instead.
    rxrNameCell: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        borderRightWidth: BORDER,
        borderStyle: "solid",
        borderColor: COLORS.black,
        justifyContent: "center",
    },
    rxrValueCell: {
        flexGrow: 1,
        flexShrink: 0,
        flexBasis: 0,
        justifyContent: "center",
        alignItems: "flex-end",
    },
    rxrValueText: {
        color: COLORS.blue,
        fontSize: 7,
        fontFamily: "Helvetica-Bold",
        marginHorizontal: 4,
    },
});
